import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { listTeamRoster, createInvite, getCurrentUser } from '@/lib/auth';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  CheckSquare,
  Plus,
  Calendar as CalendarIcon,
  AlertCircle,
  Trash2,
  Pencil,
  Search,
  Filter,
  BarChart3,
  ListChecks,
} from 'lucide-react';
import { tasksAPI } from '@/lib/api';
import { useProject } from '@/lib/ProjectContext';
import { toast } from 'sonner';
import { useT } from '@/lib/LanguageContext';
import { format, isBefore, isToday, parseISO } from 'date-fns';
import TaskPanel from './TaskPanel';
import TaskListView from './TaskListView';
import { taskListsAPI } from '@/lib/api';

const STATUS_COLUMNS = [
  { id: 'backlog', label: 'To do' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'review', label: 'Review' },
  { id: 'done', label: 'Done' },
];

const TYPE_OPTIONS = [
  { value: 'internal', label: 'Internal' },
  { value: 'dev', label: 'Dev' },
  { value: 'design', label: 'Design' },
  { value: 'marketing_material', label: 'Marketing' },
  { value: 'client_request', label: 'Client Request' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const PRIORITY_STYLES = {
  low: 'bg-slate-100 text-slate-700 border-slate-300',
  medium: 'bg-blue-100 text-blue-700 border-blue-300',
  high: 'bg-amber-100 text-amber-800 border-amber-300',
  urgent: 'bg-red-100 text-red-700 border-red-300',
};

const TYPE_STYLES = {
  dev: 'bg-purple-100 text-purple-700',
  design: 'bg-pink-100 text-pink-700',
  marketing_material: 'bg-teal-100 text-teal-700',
  client_request: 'bg-amber-100 text-amber-800',
  internal: 'bg-slate-100 text-slate-700',
};

const ALL = '__all__';
const NONE = '__none__';
const UNASSIGNED = '__unassigned__';
const OTHER = '__other__';

const emptyForm = {
  title: '',
  description: '',
  type: 'internal',
  priority: 'medium',
  status: 'backlog',
  assigned_to: '',
  due_date: '',
  project_id: '',
};

const toIsoOrNull = (localValue) => {
  if (!localValue) return null;
  const d = new Date(localValue);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const toLocalInput = (iso) => {
  if (!iso) return '';
  try {
    const d = typeof iso === 'string' ? parseISO(iso) : new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    // yyyy-MM-ddTHH:mm in local tz, for <input type="datetime-local">
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
};

const TaskCard = ({ task, onEdit, onToggleDone, onDelete, onOpenDetail, projectBadge }) => {
  const due = task.due_date ? parseISO(task.due_date) : null;
  const overdue = due && !isToday(due) && isBefore(due, new Date()) && task.status !== 'done';
  const dueToday = due && isToday(due);
  const progress = task.progress_percent || 0;

  return (
    <div
      className="border border-[#E3DDD1] rounded-lg bg-white p-3 shadow-sm hover:shadow transition cursor-pointer"
      data-testid={`task-card-${task._id}`}
      onClick={() => onOpenDetail(task)}
    >
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={task.status === 'done'}
          onChange={(e) => {
            e.stopPropagation();
            onToggleDone(task);
          }}
          className="mt-1 h-4 w-4 rounded border-[#D3CCBE]"
          data-testid={`task-complete-${task._id}`}
          aria-label="Mark complete"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p
              className={`text-sm font-semibold text-[#1E2A33] ${
                task.status === 'done' ? 'line-through text-[#5C6670]' : ''
              }`}
              style={{ fontFamily: 'Manrope' }}
            >
              {task.title}
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(task);
                }}
                data-testid={`task-edit-${task._id}`}
                aria-label="Edit task"
              >
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-red-600 hover:text-red-700"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(task);
                }}
                data-testid={`task-delete-${task._id}`}
                aria-label="Delete task"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          {task.description && (
            <p className="text-xs text-[#5C6670] mt-1 line-clamp-2">{task.description}</p>
          )}

          {/* Progress bar */}
          {progress > 0 && (
            <div className="mt-2">
              <div className="w-full h-1.5 bg-[#E3DDD1] rounded-full overflow-hidden">
                <div
                  className={`h-1.5 rounded-full transition-all ${
                    progress === 100 ? 'bg-green-500' : progress >= 70 ? 'bg-blue-500' : progress >= 40 ? 'bg-amber-500' : 'bg-gray-400'
                  }`}
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-0.5">
                <span className="text-[10px] text-[#5C6670]">{progress}%</span>
                {task.subtask_count > 0 && (
                  <span className="text-[10px] text-[#5C6670] flex items-center gap-0.5">
                    <ListChecks className="w-3 h-3" />
                    {task.completed_subtasks || 0}/{task.subtask_count}
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {projectBadge && (
              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-white border border-[#E3DDD1] text-[#5C6670]">
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: projectBadge.color || '#A62B1F' }} />
                {projectBadge.name}
              </span>
            )}
            <Badge
              variant="outline"
              className={`text-[10px] uppercase tracking-wide ${PRIORITY_STYLES[task.priority] || ''}`}
            >
              {task.priority}
            </Badge>
            {task.type && (
              <span
                className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${TYPE_STYLES[task.type] || TYPE_STYLES.internal}`}
              >
                {(TYPE_OPTIONS.find((t) => t.value === task.type)?.label) || task.type}
              </span>
            )}
            {due && (
              <span
                className={`inline-flex items-center gap-1 text-[11px] ${
                  overdue
                    ? 'text-red-600'
                    : dueToday
                    ? 'text-amber-700'
                    : 'text-[#5C6670]'
                }`}
                data-testid={`task-due-${task._id}`}
              >
                {overdue ? <AlertCircle className="w-3 h-3" /> : <CalendarIcon className="w-3 h-3" />}
                {format(due, 'MMM d')}
              </span>
            )}
            {task.assigned_to && (
              <span className="text-[11px] text-[#5C6670] truncate max-w-[140px]">
                @{task.assigned_to}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function TaskBoard() {
  const t = useT();
  const { activeProject, projects, loading: projectLoading } = useProject();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  // Which project's tasks to show — ALL means every project
  const [projectFilter, setProjectFilter] = useState(ALL);

  const [statusFilter, setStatusFilter] = useState(ALL);
  const [priorityFilter, setPriorityFilter] = useState(ALL);
  const [typeFilter, setTypeFilter] = useState(ALL);
  const [search, setSearch] = useState('');

  const location = useLocation();
  const [dialogOpen, setDialogOpen] = useState(false);

  // List (default) or board, remembered per browser
  const [view, setViewState] = useState(() => { try { return localStorage.getItem('stagehead.tasks.view') || 'list'; } catch { return 'list'; } });
  const setView = (v) => { setViewState(v); try { localStorage.setItem('stagehead.tasks.view', v); } catch { /* private mode */ } };
  const [focusAdd, setFocusAdd] = useState(0);
  const [taskLists, setTaskLists] = useState([]);
  useEffect(() => { if (view === 'board') taskListsAPI.list().then((r) => setTaskLists(r.data || [])).catch(() => {}); }, [view]);

  // "New task" from the global New button: focus the quick-add line in list view, open the form on the board
  useEffect(() => {
    if (location.state?.openNew) {
      if (view === 'list') setFocusAdd((n) => n + 1); else setDialogOpen(true);
      window.history.replaceState({}, '');
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps
  const [editingTask, setEditingTask] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);

  const [detailTask, setDetailTask] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const [teamRoster, setTeamRoster] = useState([]);
  const [invitingAssignee, setInvitingAssignee] = useState(false);
  const [assigneeOtherMode, setAssigneeOtherMode] = useState(false);
  const currentUser = getCurrentUser();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'owner';

  useEffect(() => {
    listTeamRoster().then(setTeamRoster).catch(() => setTeamRoster([]));
  }, []);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const params = (projectFilter !== ALL && projectFilter !== NONE) ? { project_id: projectFilter, limit: 200 } : { limit: 200 };
      const res = await tasksAPI.list(params);
      const list = Array.isArray(res.data) ? res.data : [];
      setTasks(projectFilter === NONE ? list.filter(t => !t.project_id) : list);
    } catch (err) {
      console.error('Failed to load tasks:', err);
      toast.error('Failed to load tasks');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  // Default project filter to activeProject when it first loads
  useEffect(() => {
    if (!projectLoading && activeProject?._id && projectFilter === ALL) {
      setProjectFilter(activeProject._id);
    }
  }, [projectLoading, activeProject?._id]);

  useEffect(() => {
    if (!projectLoading && view === 'board') loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectFilter, projectLoading, view]);

  const openDetail = (task) => {
    setDetailTask(task);
    setDetailOpen(true);
  };

  const openCreate = () => {
    setEditingTask(null);
    setForm({ ...emptyForm });
    setAssigneeOtherMode(false);
    setDialogOpen(true);
  };

  const openEdit = (task) => {
    setEditingTask(task);
    setForm({
      title: task.title || '',
      description: task.description || '',
      type: task.type || 'internal',
      priority: task.priority || 'medium',
      status: task.status || 'backlog',
      assigned_to: task.assigned_to || '',
      due_date: toLocalInput(task.due_date),
    });
    setAssigneeOtherMode(!!task.assigned_to && !teamRoster.some((m) => m.email === task.assigned_to));
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    const rawProjectId = projectFilter !== ALL ? projectFilter : form.project_id;
    const createProjectId = (rawProjectId && rawProjectId !== NONE) ? rawProjectId : null;
    setSubmitting(true);
    try {
      if (editingTask) {
        const payload = {
          title: form.title.trim(),
          description: form.description || null,
          type: form.type,
          priority: form.priority,
          status: form.status,
          assigned_to: form.assigned_to.trim() || null,
          due_date: toIsoOrNull(form.due_date),
        };
        await tasksAPI.update(editingTask._id, payload);
        toast.success('Task updated');
      } else {
        const payload = {
          project_id: createProjectId,
          title: form.title.trim(),
          description: form.description || null,
          type: form.type,
          priority: form.priority,
          status: form.status,
          assigned_to: form.assigned_to.trim() || null,
          due_date: toIsoOrNull(form.due_date),
        };
        await tasksAPI.create(payload);
        toast.success('Task created');
      }
      setDialogOpen(false);
      setEditingTask(null);
      setForm({ ...emptyForm });
      await loadTasks();
    } catch (err) {
      console.error('Task save failed:', err);
      const detail = err?.response?.data?.detail || 'Failed to save task';
      toast.error(typeof detail === 'string' ? detail : 'Failed to save task');
    } finally {
      setSubmitting(false);
    }
  };

  const inviteAssignee = async () => {
    const email = form.assigned_to.trim();
    if (!email) return;
    setInvitingAssignee(true);
    try {
      await createInvite({ email, role: 'member' });
      toast.success(`Invite sent to ${email}`);
    } catch (err) {
      toast.error(err?.message || 'Failed to send invite');
    } finally {
      setInvitingAssignee(false);
    }
  };

  const toggleDone = async (task) => {
    const next = task.status === 'done' ? 'backlog' : 'done';
    try {
      await tasksAPI.update(task._id, { status: next });
      setTasks((prev) => prev.map((t) => (t._id === task._id ? { ...t, status: next } : t)));
    } catch (err) {
      console.error('Toggle failed:', err);
      toast.error('Failed to update task');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await tasksAPI.delete(deleteTarget._id);
      setTasks((prev) => prev.filter((t) => t._id !== deleteTarget._id));
      toast.success('Task deleted');
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Failed to delete task');
    } finally {
      setDeleteTarget(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tasks.filter((t) => {
      if (statusFilter !== ALL && t.status !== statusFilter) return false;
      if (priorityFilter !== ALL && t.priority !== priorityFilter) return false;
      if (typeFilter !== ALL && t.type !== typeFilter) return false;
      if (q) {
        const hay = `${t.title || ''} ${t.description || ''} ${t.assigned_to || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tasks, statusFilter, priorityFilter, typeFilter, search]);

  const columns = useMemo(() => {
    const byStatus = Object.fromEntries(STATUS_COLUMNS.map((c) => [c.id, []]));
    filtered.forEach((t) => {
      const key = byStatus[t.status] ? t.status : 'backlog';
      byStatus[key].push(t);
    });
    return STATUS_COLUMNS.map((c) => ({ ...c, tasks: byStatus[c.id] }));
  }, [filtered]);

  if (projectLoading) {
    return (
      <Layout title="Task Board">
        <div className="flex items-center justify-center h-96">
          <p className="text-[#5C6670]" style={{ fontFamily: 'Manrope' }}>{t('common.loading')}</p>
        </div>
      </Layout>
    );
  }

  if (view === 'list') {
    return (
      <Layout title="Tasks">
        <TaskListView projects={projects} roster={teamRoster} focusSignal={focusAdd} onShowBoard={() => setView('board')} />
      </Layout>
    );
  }

  const selectedProjectName = projectFilter === ALL
    ? t('tasks.all_projects')
    : (projects.find(p => p._id === projectFilter)?.name || t('common.project'));

  const totalCount = tasks.length;
  const filteredCount = filtered.length;

  return (
    <Layout title="Tasks">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-[#1E2A33]">
              <CheckSquare className="w-5 h-5" />
              {t('tasks.title')}
            </CardTitle>
            <p className="text-sm text-[#5C6670] mt-1">
              {selectedProjectName} — {totalCount} task{totalCount === 1 ? '' : 's'}
              {filteredCount !== totalCount && ` (${filteredCount} shown)`}
            </p>
          </div>
          <div className="flex gap-2">
          <Button variant="outline" onClick={() => setView('list')} data-testid="task-view-list">
            <ListChecks className="w-4 h-4 mr-2" />List
          </Button>
          <Button
            onClick={openCreate}
            className="bg-[#A62B1F] hover:bg-[#8C2318] text-white"
            data-testid="task-create-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('tasks.new_task')}
          </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {/* Project selector */}
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="w-[180px]" data-testid="task-filter-project">
                <SelectValue placeholder="Project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t('tasks.all_projects')}</SelectItem>
                <SelectItem value={NONE}>
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full inline-block flex-shrink-0 border border-dashed border-[#9AA3AB]" />
                    No project
                  </span>
                </SelectItem>
                {projects.map(p => (
                  <SelectItem key={p._id} value={p._id}>
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: p.color || '#A62B1F' }} />
                      {p.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="w-px h-6 bg-[#E3DDD1]" />

            <div className="relative flex-1 min-w-[180px] max-w-xs">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6670]" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('tasks.search')}
                className="pl-9"
                data-testid="task-search"
              />
            </div>
            <div className="flex items-center gap-1 text-[#5C6670]">
              <Filter className="w-4 h-4" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="task-filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All statuses</SelectItem>
                {STATUS_COLUMNS.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-[150px]" data-testid="task-filter-priority">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t('tasks.all_priorities')}</SelectItem>
                {PRIORITY_OPTIONS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[160px]" data-testid="task-filter-type">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t('tasks.all_types')}</SelectItem>
                {TYPE_OPTIONS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Board */}
          {loading ? (
            <div className="py-12 text-center text-[#5C6670]">{t('tasks.loading')}</div>
          ) : totalCount === 0 ? (
            <div className="py-16 text-center">
              <CheckSquare className="w-10 h-10 mx-auto text-[#D3CCBE]" />
              <p className="mt-3 text-[#1E2A33] font-semibold" style={{ fontFamily: 'Manrope' }}>
                {t('tasks.none_found')}
              </p>
              <p className="text-sm text-[#5C6670] mt-1">
                Create your first task for {selectedProjectName}.
              </p>
              <Button
                onClick={openCreate}
                className="mt-4 bg-[#A62B1F] hover:bg-[#8C2318] text-white"
                data-testid="task-empty-create"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('tasks.new_task')}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {columns.map((col) => (
                <div
                  key={col.id}
                  className="bg-[#FBF8F2] rounded-lg p-3 min-h-[200px] border border-[#E3DDD1]"
                  data-testid={`task-column-${col.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3
                      className="text-sm font-semibold text-[#1E2A33]"
                      style={{ fontFamily: 'Manrope' }}
                    >
                      {col.label}
                    </h3>
                    <span className="text-xs text-[#5C6670] bg-white rounded-full px-2 py-0.5 border border-[#E3DDD1]">
                      {col.tasks.length}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {col.tasks.length === 0 ? (
                      <p className="text-xs text-[#9AA3AB] py-6 text-center">{t('tasks.none_found')}</p>
                    ) : (
                      col.tasks.map((task) => (
                        <TaskCard
                          key={task._id}
                          task={task}
                          onEdit={openEdit}
                          onToggleDone={toggleDone}
                          onDelete={setDeleteTarget}
                          onOpenDetail={openDetail}
                          projectBadge={projectFilter === ALL ? projects.find(p => p._id === task.project_id) : null}
                        />
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Task panel: everything about one task on one page */}
      <TaskPanel
        task={detailTask && tasks.find((x) => x._id === detailTask._id)}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onChanged={(nt) => setTasks((prev) => prev.map((x) => (x._id === nt._id ? nt : x)))}
        onDeleted={(id) => { setTasks((prev) => prev.filter((x) => x._id !== id)); setDetailOpen(false); setDetailTask(null); }}
        projects={projects}
        lists={taskLists}
        roster={teamRoster}
      />

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[560px]" data-testid="task-dialog">
          <DialogHeader>
            <DialogTitle>{editingTask ? t('tasks.edit_task') : t('tasks.new_task')}</DialogTitle>
            <DialogDescription>
              {editingTask
                ? 'Update the task details below.'
                : `Add a task to ${selectedProjectName}.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Project selector — only shown when creating in "All Projects" view */}
            {!editingTask && projectFilter === ALL && (
              <div>
                <Label>{t('common.project')}</Label>
                <Select
                  value={form.project_id || NONE}
                  onValueChange={(v) => setForm((f) => ({ ...f, project_id: v === NONE ? '' : v }))}
                >
                  <SelectTrigger data-testid="task-form-project">
                    <SelectValue placeholder="Select a project..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>
                      <span className="flex items-center gap-2 text-[#5C6670]">
                        <span className="w-2 h-2 rounded-full inline-block flex-shrink-0 border border-dashed border-[#9AA3AB]" />
                        No project
                      </span>
                    </SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p._id} value={p._id}>
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: p.color || '#A62B1F' }} />
                          {p.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label htmlFor="task-title">{t('tasks.task_title')}</Label>
              <Input
                id="task-title"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="What needs to be done?"
                data-testid="task-form-title"
              />
            </div>
            <div>
              <Label htmlFor="task-desc">{t('tasks.description')}</Label>
              <Textarea
                id="task-desc"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Optional details..."
                rows={3}
                data-testid="task-form-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              {editingTask && <div>
                <Label>{t('tasks.type')}</Label>
                <Select
                  value={form.type}
                  onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}
                >
                  <SelectTrigger data-testid="task-form-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPE_OPTIONS.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>}
              <div>
                <Label>{t('tasks.priority')}</Label>
                <Select
                  value={form.priority}
                  onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}
                >
                  <SelectTrigger data-testid="task-form-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('common.status')}</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}
                >
                  <SelectTrigger data-testid="task-form-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_COLUMNS.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="task-due">{t('tasks.due_date')}</Label>
                <Input
                  id="task-due"
                  type="datetime-local"
                  value={form.due_date}
                  onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
                  data-testid="task-form-due"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="task-assigned">{t('tasks.assignee')}</Label>
              <Select
                value={assigneeOtherMode ? OTHER : (form.assigned_to || UNASSIGNED)}
                onValueChange={(v) => {
                  if (v === UNASSIGNED) { setAssigneeOtherMode(false); setForm((f) => ({ ...f, assigned_to: '' })); }
                  else if (v === OTHER) { setAssigneeOtherMode(true); setForm((f) => ({ ...f, assigned_to: '' })); }
                  else { setAssigneeOtherMode(false); setForm((f) => ({ ...f, assigned_to: v })); }
                }}
              >
                <SelectTrigger id="task-assigned" data-testid="task-form-assigned">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                  {teamRoster.map((m) => (
                    <SelectItem key={m.email} value={m.email}>{m.full_name || m.email}</SelectItem>
                  ))}
                  <SelectItem value={OTHER}>Someone else (not on the team)…</SelectItem>
                </SelectContent>
              </Select>
              {assigneeOtherMode && (
                <div className="mt-2 space-y-1.5">
                  <Input
                    type="email"
                    value={form.assigned_to}
                    onChange={(e) => setForm((f) => ({ ...f, assigned_to: e.target.value }))}
                    placeholder="name@example.com"
                    autoFocus
                  />
                  <p className="text-xs text-muted-foreground">
                    They're not a Stagehead OS teammate — they'll get a one-off email about this task instead of an in-app notification.
                  </p>
                  {isAdmin && form.assigned_to.trim() && (
                    <Button type="button" size="sm" variant="outline" onClick={inviteAssignee} disabled={invitingAssignee}>
                      {invitingAssignee ? 'Sending invite…' : 'Invite them to Stagehead OS instead'}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={submitting}
              data-testid="task-form-cancel"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={submit}
              disabled={submitting}
              className="bg-[#A62B1F] hover:bg-[#8C2318] text-white"
              data-testid="task-form-save"
            >
              {submitting ? 'Saving...' : editingTask ? t('common.save') : t('tasks.create_task')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('tasks.delete_task')}</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.title}" will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="task-delete-cancel">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="task-delete-confirm"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
